import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { AdminBottomNav } from './AdminBottomNav';
import {
  ADMIN_GROUPS,
  ADMIN_NAV_ITEMS,
  canAccessAdminTab,
  getGroupFromPath,
} from '../utils/adminNavConfig';
import type { AdminGroup } from '../utils/adminNavConfig';
import { Home } from 'lucide-react';
import { UserMenu } from '@/components/layout/UserMenu';
import { NotificationBell } from '@/features/notifications';
import { ViewAsOverlay } from '@/components/ImpersonationBanner';
import { useAuthStore } from '@/features/auth';
import { ADMIN_COLORS } from '../utils/adminAnimations';
import { AnimatedGradientBackground } from '@/components/glass';
import { NeonBorder } from '@/components/layout/NeonLoadingLine';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { StickyChatWidget } from '@/features/messaging';
import { useGlobalNotifications } from '@/features/messaging/hooks';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { NotificationGate } from '@/components/NotificationGate';

const SIDEBAR_W = '8rem';
const FLOAT_GAP = 12; // px — floating gap around nav panes
const ACCENT_ACTIVE = 'var(--admin-nav-active-text)';
const GLASS_BORDER = 'var(--admin-nav-border)';
const MUTED = 'var(--admin-nav-text)';

/**
 * AdminSidebarLayout
 * Dedicated admin layout with white sidebar, grouped navigation,
 * header with sub-tabs, and mobile bottom nav.
 */
export function AdminSidebarLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const [showHomeConfirm, setShowHomeConfirm] = useState(false);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);

  const isMobile = useMediaQuery(MD_BREAKPOINT);

  // Global notification WebSocket — instant conversation list updates
  useGlobalNotifications();

  // FCM push notifications — web + native (mandatory)
  const { pushBlocked, enableNotifications, checkPushGranted } = usePushNotifications();

  const isActive = (path: string) =>
    path === '/admin'
      ? location.pathname === '/admin'
      : location.pathname === path || location.pathname.startsWith(path + '/');

  // Derive the active group from the current route
  const activeGroup: AdminGroup = useMemo(
    () => getGroupFromPath(location.pathname) ?? 'oversight',
    [location.pathname],
  );

  // Filter nav items by RBAC
  const visibleItems = useMemo(
    () => ADMIN_NAV_ITEMS.filter(item => canAccessAdminTab(item.tabId, permissions)),
    [permissions],
  );

  // Only show groups that have at least one visible item
  const visibleGroups = useMemo(
    () => ADMIN_GROUPS.filter(g => visibleItems.some(item => item.group === g.id)),
    [visibleItems],
  );

  // Sub-items for the currently active group (shown in header)
  const activeGroupItems = useMemo(
    () => visibleItems.filter(item => item.group === activeGroup),
    [visibleItems, activeGroup],
  );

  /** Navigate to the first sub-item when clicking a group button */
  const handleGroupClick = (groupId: AdminGroup) => {
    const firstItem = visibleItems.find(i => i.group === groupId);
    if (firstItem) navigate(firstItem.path);
  };

  return (
    <div
      className="min-h-dvh relative"
      style={{
        fontFamily: 'var(--font-sans)',
        color: 'var(--admin-page-text)',
        backgroundColor: 'var(--admin-page-bg)',
      }}
    >
      <AnimatedGradientBackground />
      {/* Immersive impersonation overlay */}
      <ViewAsOverlay />
      {/* Fixed Admin Sidebar - Hidden on mobile */}
      {!isMobile && (
        <aside
          className="fixed z-40 flex flex-col rounded-xl transition-all duration-300 ease-in-out glass-surface-nav"
          style={{
            top: `calc(env(safe-area-inset-top, 0px) + ${FLOAT_GAP}px + var(--view-as-offset, 0px))`,
            left: FLOAT_GAP,
            bottom: FLOAT_GAP,
            width: SIDEBAR_W,
            backgroundColor: 'var(--admin-nav-bg)',
            borderColor: 'var(--admin-nav-border)',
          }}
        >
          <NeonBorder variant="admin" />
          <nav className="flex flex-col items-center gap-2.5 px-2 py-4">
            {visibleGroups.map((group) => {
              const Icon = group.icon;
              const isGroupActive = activeGroup === group.id;
              return (
                <button
                  key={group.id}
                  onClick={() => handleGroupClick(group.id)}
                  title={t(group.labelKey)}
                  className={`w-full flex flex-col items-center gap-1.5 rounded-xl px-1.5 py-3 transition-all duration-200 ${
                    isGroupActive ? '' : 'nav-hover-admin'
                  }`}
                  style={{
                    color: isGroupActive ? ACCENT_ACTIVE : MUTED,
                    backgroundColor: isGroupActive ? 'var(--admin-nav-active-bg)' : undefined,
                  }}
                >
                  <span className={`flex-shrink-0 transition-all duration-200 ${isGroupActive ? 'nav-icon-glow scale-110' : ''}`}>
                    <Icon size={26} strokeWidth={isGroupActive ? 2.5 : 2} />
                  </span>
                  <span
                    style={{
                      fontSize: '9px',
                      fontWeight: 700,
                      fontFamily: 'var(--font-sans)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      lineHeight: 1.2,
                      textAlign: 'center',
                    }}
                  >
                    {t(group.labelKey)}
                  </span>
                </button>
              );
            })}

            {/* Divider */}
            <div
              className="w-full"
              style={{
                borderTop: `1px solid ${GLASS_BORDER}`,
                marginTop: '4px',
                marginBottom: '4px',
              }}
            />

            {/* Home */}
            <button
              className="w-full flex flex-col items-center gap-1.5 rounded-xl px-1.5 py-3 transition-all duration-150 nav-hover-admin"
              style={{
                color: MUTED,
              }}
              title={t('nav.switchToHome')}
              onClick={() => setShowHomeConfirm(true)}
            >
              <Home size={26} strokeWidth={2} className="flex-shrink-0" />
              <span
                style={{
                  fontSize: '9px',
                  fontWeight: 700,
                  fontFamily: 'var(--font-sans)',
                  textTransform: 'uppercase',
                }}
              >
                {t('nav.switchToHome')}
              </span>
            </button>
          </nav>
        </aside>
      )}

      {/* Main Content Area */}
      <div
        className="flex-1 flex flex-col"
        style={{
          marginLeft: isMobile ? 0 : `calc(${SIDEBAR_W} + ${FLOAT_GAP}px)`,
          transition: 'margin-left 0.3s ease-in-out',
        }}
      >
        {/* Fixed Header - admin badge + sub-category tabs */}
        <header
          className="fixed z-30 flex-shrink-0 glass-surface-nav rounded-xl"
          /* header also themed via admin nav variables */
          style={{
            top: `calc(env(safe-area-inset-top, 0px) + ${FLOAT_GAP}px + var(--view-as-offset, 0px))`,
            right: FLOAT_GAP,
            left: isMobile ? FLOAT_GAP : `calc(${SIDEBAR_W} + ${FLOAT_GAP * 2 + 4}px)`,
            transition: 'left 0.3s ease-in-out, top 0.5s ease-in-out',
            backgroundColor: 'var(--admin-nav-bg)',
            borderColor: 'var(--admin-nav-border)',
          }}
        >
          {/* Single row: sub-category tabs (left) + user menu (right) — force LTR so avatar stays on the right */}
          <div
            dir="ltr"
            className={`h-12 flex items-center ${
              isMobile ? 'px-4' : 'px-4 sm:px-6'
            }`}
          >
            {/* Sub-category tabs */}
            <div className="flex items-center gap-1 overflow-x-auto flex-1 hide-scrollbar">
              {activeGroupItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all duration-200 whitespace-nowrap ${
                      active ? '' : 'nav-hover-admin'
                    }`}
                    style={{
                      color: active ? ACCENT_ACTIVE : MUTED,
                      backgroundColor: active ? 'var(--admin-nav-active-bg)' : undefined,
                      fontSize: '12px',
                      fontWeight: active ? 700 : 500,
                      textDecoration: 'none',
                    }}
                  >
                    <span className={`flex-shrink-0 transition-all duration-200 ${active ? 'nav-icon-glow' : ''}`}>
                      <Icon size={14} strokeWidth={active ? 2.5 : 2} />
                    </span>
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </div>

            {/* Right side: notifications + user menu — scaled down for compact admin header */}
            <div className="flex items-center gap-2 flex-shrink-0" style={{ transform: 'scale(0.78)', transformOrigin: 'center right' }}>
              <NotificationBell />
              <UserMenu />
            </div>
          </div>
          <NeonBorder variant="admin" />
        </header>

        {/* Main Content - scrollable */}
        <main
          className="flex-1 overflow-y-auto"
          style={{
            marginTop: `calc(env(safe-area-inset-top, 0px) + 3rem + ${FLOAT_GAP * 2}px + var(--view-as-offset, 0px))`,
            paddingBottom: isMobile ? 'calc(4rem + env(safe-area-inset-bottom, 0px))' : 0,
            transition: 'margin-top 0.5s ease-in-out, filter 0.3s ease-in-out',
            filter: isImpersonating ? 'brightness(0.98) saturate(0.9)' : undefined,
            '--page-card-bg': 'var(--admin-page-card-bg)',
            '--page-card-border': 'var(--admin-page-card-border)',
          } as React.CSSProperties & Record<string, string>}
        >
          <div className="max-w-7xl py-6 px-4 sm:px-6">
            <Breadcrumbs />
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom Navigation - Mobile only */}
      {isMobile && <AdminBottomNav />}

      {/* Sticky chat widget — desktop only */}
      {!isMobile && <StickyChatWidget />}

      {/* "Switch Back to Home?" confirmation modal */}
      {showHomeConfirm && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/50"
            onClick={() => setShowHomeConfirm(false)}
          />
          <div
            className="fixed z-[70] flex flex-col items-center gap-4 rounded-xl px-8 py-7 glass-surface-modal"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              minWidth: '280px',
              maxWidth: '360px',
            }}
          >
            <Home size={36} strokeWidth={2} style={{ color: ACCENT_ACTIVE }} />
            <span
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#1e293b',
                textAlign: 'center',
              }}
            >
              {t('adminNav.switchHomeTitle')}
            </span>
            <div className="flex gap-3 w-full">
              <button
                className="flex-1 py-3 rounded-lg font-bold uppercase text-xs transition-all active:translate-y-px"
                style={{
                  backgroundColor: ACCENT_ACTIVE,
                  color: '#ffffff',
                  border: `1px solid ${GLASS_BORDER}`,
                  boxShadow: ADMIN_COLORS.shadow,
                }}
                onClick={() => {
                  setShowHomeConfirm(false);
                  navigate('/home');
                }}
              >
                {t('adminNav.switchHomeConfirm')}
              </button>
              <button
                className="flex-1 py-3 rounded-lg font-bold uppercase text-xs transition-all active:translate-y-px"
                style={{
                  backgroundColor: 'transparent',
                  color: MUTED,
                  border: `1px solid ${MUTED}`,
                }}
                onClick={() => setShowHomeConfirm(false)}
              >
                {t('adminNav.switchHomeCancel')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Full-screen notification gate — blocks app until push is granted */}
      <NotificationGate
        blocked={pushBlocked}
        onEnable={enableNotifications}
        onCheck={checkPushGranted}
      />
    </div>
  );
}
